import LegalLayout, { LegalSection } from "@/components/legal/LegalLayout";

export default function Terms() {
  return (
    <LegalLayout
      title="Terms of Service"
      eyebrow="Legal · Terms"
      updated="December 2025"
    >
      <p className="text-lg text-zinc-300 font-light leading-relaxed">
        Plain English where possible, lawyer English where necessary. By
        engaging Invisible System Design, you agree to the following terms.
      </p>

      <LegalSection n={1} title="The agreement">
        <p>
          These Terms govern your use of ISD's services. Specific commercial
          terms (scope, pricing, SLAs) are set out in a separate Order Form
          or written confirmation by email. Where the two conflict, the Order
          Form prevails.
        </p>
      </LegalSection>

      <LegalSection n={2} title="What we do">
        <p>
          We design, build, deploy, and maintain conversation-native software
          that runs inside the messaging platforms you authorize. We provide
          human engineering oversight on every change. We do not provide the
          messaging platforms themselves — those remain operated by Apple,
          Meta, Telegram, Slack, and their respective owners.
        </p>
      </LegalSection>

      <LegalSection n={3} title="Subscription & billing">
        <ul className="list-none space-y-2 pl-0">
          {[
            ["Plans", "Light · Standard · Priority. Monthly subscription."],
            ["Change requests", "Counted per the plan you're on. Unused requests do not roll over."],
            ["Within reason", "Priority plan is unlimited within reason; we'll flag if scope changes shape entirely."],
            ["Payment", "Charged monthly in advance. Failed payments may pause service after 7 days' notice."],
            ["Cancellation", "Cancel anytime, effective end of the current billing period. No exit fees."],
          ].map(([k, v]) => (
            <li key={k} className="flex gap-4 border-l border-white/10 pl-4">
              <span className="text-yellow-400 font-mono text-xs uppercase tracking-[0.22em] shrink-0 w-32 pt-1">{k}</span>
              <span>{v}</span>
            </li>
          ))}
        </ul>
      </LegalSection>

      <LegalSection n={4} title="The email-based workflow">
        <p>
          Change requests are submitted by email and processed asynchronously.
          Stated turnaround times are targets, not guarantees, and exclude
          weekends, public holidays in the relevant jurisdiction, and
          properly notified incident windows.
        </p>
      </LegalSection>

      <LegalSection n={5} title="Your responsibilities">
        <p>
          You will provide accurate information, lawful use cases, and any
          third-party credentials (e.g., platform API keys) required to
          operate the system. You agree not to use ISD-built systems for
          spam, fraud, harassment, or anything that violates a messaging
          platform's terms.
        </p>
      </LegalSection>

      <LegalSection n={6} title="Intellectual property">
        <p>
          On full payment, you own the deliverables custom-built for your
          system. We retain ownership of the underlying ISD core libraries,
          tooling, and methodology used across clients. You receive a
          perpetual, royalty-free licence to use those libraries within the
          system we deliver.
        </p>
      </LegalSection>

      <LegalSection n={7} title="Service levels & availability">
        <p>
          Target uptime is 99.9% for Standard and Priority plans, measured
          monthly excluding scheduled maintenance and force majeure. Service
          credits, where applicable, are detailed in the Order Form. The
          Light plan is best-effort.
        </p>
      </LegalSection>

      <LegalSection n={8} title="Confidentiality">
        <p>
          Each party will treat the other's non-public information as
          confidential and use it only to perform under this agreement.
          NDAs and DPAs are available; we'll sign yours or send ours.
        </p>
      </LegalSection>

      <LegalSection n={9} title="Liability">
        <p>
          Neither party is liable for indirect, incidental, or consequential
          damages. Our aggregate liability in any 12-month period is limited
          to the fees paid by you to ISD in that period. Nothing in these
          Terms limits liability that cannot be limited under applicable law.
        </p>
      </LegalSection>

      <LegalSection n={10} title="Termination & exit">
        <p>
          Either party may terminate for material breach with 30 days'
          written notice and a chance to cure. On termination we will provide
          architecture documentation, credentials, and a clean data export —
          no hostage data, no exit fees.
        </p>
      </LegalSection>

      <LegalSection n={11} title="Changes to these Terms">
        <p>
          We may update these Terms occasionally. Material changes will be
          emailed to known contacts at least 30 days before they take effect.
          Continued use after that date constitutes acceptance.
        </p>
      </LegalSection>

      <LegalSection n={12} title="Contact">
        <p>
          Questions about these Terms:{" "}
          <a
            href="mailto:Robert@InvisibleSystem.Design?subject=Terms%20question"
            className="text-yellow-400 hover:underline"
            data-testid="terms-contact-email"
          >
            Robert@InvisibleSystem.Design
          </a>
          .
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
